import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { GlobalSearchBar } from 'features/global-search'
import { Tooltip } from 'components/ds2/Tooltip'
import { Magnifier } from './icons'
import { ROW_BASE, ROW_IDLE } from './styles'

/**
 * Sidebar entry point for global search — replaces the old fixed bar that used
 * to sit above the main content (`MasterLayout.tsx`). Mirrors the prototype's
 * sidebar search trigger (`new-agent-numeo-main/src/App.tsx` lines 1310–1322):
 * a pill-styled row with a magnifier icon, "Search" label, and a ⌘K hint,
 * opening a centered overlay on click or ⌘K/Ctrl+K from anywhere.
 *
 * The overlay reuses the real `GlobalSearchBar` feature as-is (same query
 * logic, results, recents, scopes) rather than rebuilding search — only the
 * entry point moved, not the underlying feature.
 */
export const SidebarSearchButton = ({ isOpen }: { isOpen: boolean }) => {
    const [modalOpen, setModalOpen] = useState(false)

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (!(e.metaKey || e.ctrlKey) || e.key !== 'k') return
            // Don't hijack ⌘K while the user is typing somewhere else on the page (a text field,
            // an editor, another modal's input) — only intercept when nothing is actively editable.
            const target = e.target as HTMLElement | null
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return
            e.preventDefault()
            setModalOpen(true)
        }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [])

    useEffect(() => {
        if (!modalOpen) return
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setModalOpen(false)
        }
        window.addEventListener('keydown', handleEscape)
        return () => window.removeEventListener('keydown', handleEscape)
    }, [modalOpen])

    const isMac = typeof navigator !== 'undefined' && navigator.userAgent.includes('Mac')

    // Open state mirrors the prototype's input-styled search row (bordered pill + ⌘K hint).
    // Collapsed state matches every other collapsed icon row exactly (`ROW_BASE`/`ROW_IDLE`,
    // no border) instead of a bespoke bordered box, which squeezed down to icon-only width.
    const trigger = isOpen ? (
        <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="flex h-9 w-full shrink-0 items-center gap-2 rounded-xl border border-border bg-surface px-2.5 text-foreground-muted transition-colors hover:bg-default"
        >
            <Magnifier size={15} className="shrink-0" />
            <span className="flex-1 text-left text-[13px]">Search</span>
            <span className="rounded-md border border-border bg-surface-secondary px-1.5 py-0.5 text-[11px] font-semibold text-foreground-muted">
                {isMac ? '⌘K' : 'Ctrl K'}
            </span>
        </button>
    ) : (
        <button type="button" onClick={() => setModalOpen(true)} className={[ROW_BASE, 'justify-center', ROW_IDLE].join(' ')}>
            <Magnifier size={15} className="shrink-0" />
        </button>
    )

    return (
        <>
            {isOpen ? (
                trigger
            ) : (
                <Tooltip content="Search (⌘K)" placement="right">
                    {trigger}
                </Tooltip>
            )}

            {modalOpen &&
                createPortal(
                    <div
                        className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 pt-24"
                        onMouseDown={(e) => {
                            if (e.target === e.currentTarget) setModalOpen(false)
                        }}
                    >
                        <div className="w-full max-w-[640px] px-4" onMouseDown={(e) => e.stopPropagation()}>
                            <GlobalSearchBar autoFocus onNavigate={() => setModalOpen(false)} />
                        </div>
                    </div>,
                    document.body
                )}
        </>
    )
}
